/**
 * RatingPrompt Component
 *
 * A non-intrusive rating banner that appears after the user has been
 * engaged for a while (3+ minutes AND 3+ page views). Shows once per
 * user - stored in localStorage. Slides up from the bottom, easy to dismiss.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Star, X } from 'lucide-react';
import { API_CONFIG } from '../../config/api';

const STORAGE_KEY = 'kaalagyana_rated';
const MIN_TIME_MS = 3 * 60 * 1000; // 3 minutes
const MIN_PAGE_VIEWS = 3;

interface RatingPromptProps {
  pageViewCount: number;
  currentPage: string;
}

const RatingPrompt: React.FC<RatingPromptProps> = ({ pageViewCount, currentPage }) => {
  const [visible, setVisible] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    // Already rated or dismissed - never show again
    if (localStorage.getItem(STORAGE_KEY)) return;

    const checkEligibility = () => {
      const elapsed = Date.now() - startTime.current;
      if (elapsed >= MIN_TIME_MS && pageViewCount >= MIN_PAGE_VIEWS) {
        setVisible(true);
      }
    };

    // Check periodically (every 30s) rather than on every render
    const interval = setInterval(checkEligibility, 30000);
    // Also check when page views change
    checkEligibility();

    return () => clearInterval(interval);
  }, [pageViewCount]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'dismissed');
    setVisible(false);
  };

  const handleRate = async (rating: number) => {
    setSelectedRating(rating);
    setSubmitted(true);
    localStorage.setItem(STORAGE_KEY, `rated_${rating}`);

    // Send to analytics
    try {
      const base = API_CONFIG.baseUrl || window.location.origin;
      await fetch(`${base}${API_CONFIG.endpoints.feedback}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Site rating: ${rating}/5`,
          page: currentPage,
          email: '',
        }),
      });
    } catch {
      // fire-and-forget
    }

    // Auto-hide after 2 seconds
    setTimeout(() => setVisible(false), 2000);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-500 no-print">
      <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-lg px-5 py-3 flex items-center gap-4 max-w-sm">
        {submitted ? (
          <div className="text-sm text-brand dark:text-brand-dark font-medium py-1">
            Thank you for your feedback!
          </div>
        ) : (
          <>
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              How's your experience?
            </span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="p-0.5 transition-transform hover:scale-110 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  aria-label={`Rate ${star} out of 5`}
                >
                  <Star
                    className={`h-5 w-5 transition-colors ${
                      star <= (hoveredStar || selectedRating)
                        ? 'fill-amber-500 text-amber-500'
                        : 'text-muted-foreground/40'
                    }`}
                  />
                </button>
              ))}
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Dismiss rating"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RatingPrompt;
